import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import type { CollectionScoringStatus, CollectionScoringStatusView } from '../../generated';
import { getCollectionScoringStatusV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdScoringsScoringIdGet } from '../../generated';

/*
 * GET /v1/workspaces/{workspace_id}/assets/images/collections/{collection_id}/scorings/{scoring_id}: the status of
 * one scoring, polled while it is pending. It answers the generated `CollectionScoringStatusView`. Tests answer this
 * path with `tests/mocks/collection-scoring-handlers.ts`.
 */

/** Where a scoring stands, mapped for the UI. */
export interface CollectionScoringProgress {
  id: string;
  benchmarkId: string;
  productCategoryId: string;
  /** Pending for 30 minutes turns failed; a late result can turn it ready again. */
  status: CollectionScoringStatus;
  requestedAt: string;
  readyAt: string | null;
  failureReason: string | null;
  scoredImages: number;
  totalImages: number;
  /** The images the API could not read; they settle the scoring without a score. Empty when it names none */
  failedImageIds: string[];
}

/** The status route's answer; `failed_image_ids` is optional until the spec this package is generated from has it. */
type CollectionScoringStatusResponse = CollectionScoringStatusView & { failed_image_ids?: string[] };

export function toCollectionScoringProgress(response: CollectionScoringStatusResponse): CollectionScoringProgress {
  return {
    id: response.id,
    benchmarkId: response.benchmark_id,
    productCategoryId: response.product_category_id,
    status: response.status,
    requestedAt: response.requested_at,
    readyAt: response.ready_at,
    failureReason: response.failure_reason,
    scoredImages: response.scored_images,
    totalImages: response.total_images,
    failedImageIds: response.failed_image_ids ?? [],
  };
}

export interface GetCollectionScoringQueryVariables {
  workspace_id: string;
  collection_id: string;
  scoring_id: string;
}

export type GetCollectionScoringQuery = CollectionScoringProgress;

async function getCollectionScoring({ workspace_id, collection_id, scoring_id }: GetCollectionScoringQueryVariables) {
  const { data } =
    await getCollectionScoringStatusV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdScoringsScoringIdGet({
      path: { workspace_id, collection_id, scoring_id },
      throwOnError: true,
    });
  return toCollectionScoringProgress(data);
}

export const useGetCollectionScoringQuery = <TData = GetCollectionScoringQuery, TError = unknown>(
  variables: GetCollectionScoringQueryVariables,
  options?: Omit<UseQueryOptions<GetCollectionScoringQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetCollectionScoringQuery, TError, TData>({
    queryKey: useGetCollectionScoringQuery.getKey(variables),
    queryFn: () => getCollectionScoring(variables),
    ...options,
  });
};

useGetCollectionScoringQuery.getKey = ({
  workspace_id,
  collection_id,
  scoring_id,
}: GetCollectionScoringQueryVariables) => ['GetCollectionScoring', { workspace_id, collection_id, scoring_id }];

useGetCollectionScoringQuery.fetcher = (variables: GetCollectionScoringQueryVariables) => () =>
  getCollectionScoring(variables);
